import React, { useEffect, useRef, useState } from 'react'
import { resolveVibe } from '../vibes'
import Signature from './Signature'

/**
 * THE DETONATION v2 — signature-powered.
 * Living canvas organism + engineered sonic identity + haptic choreography.
 * A whole life in about a second.
 */

export interface DetonationData {
  vibeKey: string
  vibeEmoji?: string
  vibeLabel: string
  vibeColor: string
  phrase?: string
  fromName?: string
  direction?: 'received' | 'sent'
  intensity?: number
}

interface Props {
  data: DetonationData
  onSendBack?: () => void
  onDone: () => void
}

// ─── Sonic identities — engineered per vibe, zero assets ────────────────────

function playSignatureSound(vibeKey: string, amp: number = 1.0) {
  try {
    const AC = window.AudioContext || (window as any).webkitAudioContext
    const ctx = new AC()
    const t0 = ctx.currentTime
    const master = ctx.createGain()
    master.gain.value = Math.min(1.6, amp)   // charged flavors hit harder
    master.connect(ctx.destination)

    const tone = (freq: number, at: number, dur: number, gain: number,
                  type: OscillatorType = 'sine', glideTo?: number) => {
      const o = ctx.createOscillator(), g = ctx.createGain()
      o.type = type
      o.frequency.setValueAtTime(freq, t0 + at)
      if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, t0 + at + dur)
      g.gain.setValueAtTime(0, t0 + at)
      g.gain.linearRampToValueAtTime(gain, t0 + at + 0.015)
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + at + dur)
      o.connect(g).connect(master)
      o.start(t0 + at); o.stop(t0 + at + dur + 0.05)
    }
    const noise = (at: number, dur: number, gain: number, filterFreq = 3000) => {
      const len = Math.ceil(ctx.sampleRate * dur)
      const buf = ctx.createBuffer(1, len, ctx.sampleRate)
      const d = buf.getChannelData(0)
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len)
      const src = ctx.createBufferSource(); src.buffer = buf
      const f = ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = filterFreq
      const g = ctx.createGain(); g.gain.value = gain
      src.connect(f).connect(g).connect(master)
      src.start(t0 + at)
    }

    switch (vibeKey) {
      case 'LOVE':      // two heartbeats + shimmer release
        tone(72, 0.45, 0.22, 0.30, 'sine'); tone(72, 0.62, 0.26, 0.34, 'sine')
        tone(1046, 0.82, 0.5, 0.05, 'triangle'); tone(1318, 0.86, 0.5, 0.04, 'triangle')
        break
      case 'HELD':      // slow warm chord swelling like an embrace
        tone(174, 0.05, 1.0, 0.10); tone(220, 0.12, 0.95, 0.09); tone(261, 0.2, 0.9, 0.08)
        break
      case 'I_SEE_YOU': // a single pure note that finds you and holds
        tone(523, 0.1, 1.0, 0.12); tone(523 * 2, 0.55, 0.5, 0.03)
        break
      case 'PROUD':     // ascending golden triad
        tone(392, 0.05, 0.4, 0.10); tone(494, 0.2, 0.4, 0.10); tone(587, 0.35, 0.6, 0.12)
        tone(784, 0.55, 0.5, 0.06, 'triangle')
        break
      case 'SPARK':     // electric inhale → CRACK
        noise(0.02, 0.25, 0.10, 6000)
        tone(880, 0.28, 0.06, 0.16, 'square'); noise(0.3, 0.12, 0.22, 3500)
        tone(1760, 0.32, 0.3, 0.05, 'triangle')
        break
      case 'HYPE':      // riser → bass DROP → sparkle
        tone(200, 0.0, 0.18, 0.10, 'sawtooth', 600)
        tone(160, 0.2, 0.5, 0.28, 'sine', 42)
        noise(0.2, 0.3, 0.14, 1800)
        tone(1568, 0.45, 0.3, 0.04, 'triangle')
        break
      case 'LOCKED_IN': // focus sweep → the tick of certainty
        noise(0.05, 0.6, 0.06, 900)
        tone(300, 0.05, 0.65, 0.06, 'sine', 1200)
        tone(1200, 0.75, 0.08, 0.18, 'square')
        break
      case 'AURA':      // rising crystalline arpeggio, otherworldly
        ;[523, 659, 784, 1046, 1318].forEach((f, i) => tone(f, 0.08 + i * 0.11, 0.5, 0.05, 'triangle'))
        break
      case 'MISS_YOU':  // a note that swells... and un-happens
        tone(440, 0.05, 0.55, 0.001, 'sine')
        // manual reverse-swell: quiet→loud→cut
        {
          const o = ctx.createOscillator(), g = ctx.createGain()
          o.type = 'sine'; o.frequency.value = 392
          g.gain.setValueAtTime(0.0001, t0)
          g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.6)
          g.gain.linearRampToValueAtTime(0, t0 + 0.66)
          o.connect(g).connect(master); o.start(t0); o.stop(t0 + 0.75)
        }
        break
      case 'THREE_AM':  // one deep, slow pulse in the dark
        tone(98, 0.1, 1.3, 0.16); tone(196, 0.5, 0.8, 0.04)
        break
      case 'DEAD':      // tone drops to flatline beep... silence... burst of joy
        tone(600, 0.0, 0.18, 0.10, 'sine', 220)
        tone(880, 0.24, 0.24, 0.07, 'sine')      // the flatline beeeep
        noise(0.62, 0.2, 0.18, 2500)              // resurrection burst
        tone(523, 0.64, 0.2, 0.08, 'square'); tone(659, 0.72, 0.2, 0.08, 'square')
        break
      case 'CHAOS':     // glitch stutters → sudden serene note
        ;[0, 0.09, 0.16, 0.27, 0.36, 0.48].forEach((at, i) => {
          tone(200 + Math.random() * 900, at, 0.05, 0.09, i % 2 ? 'square' : 'sawtooth')
        })
        noise(0.3, 0.15, 0.08, 4000)
        tone(880, 0.85, 0.45, 0.08, 'sine')
        break
      case 'BIRTHDAY': // music-box twinkle → wish-blow hush → party pop
        tone(523.3, 0, 0.14, 0.1, 'triangle'); tone(659.3, 0.12, 0.14, 0.1, 'triangle')
        tone(784, 0.24, 0.2, 0.11, 'triangle')
        noise(0.52, 0.1, 0.06, 900) // the blow
        tone(1046.5, 0.68, 0.3, 0.13, 'triangle'); tone(1318.5, 0.74, 0.34, 0.1, 'sine')
        break
      case 'THANK_YOU': // a warm major chord blooming, soft chime
        tone(392, 0, 0.6, 0.1, 'sine'); tone(494, 0.12, 0.55, 0.09, 'sine')
        tone(587, 0.26, 0.6, 0.08, 'triangle'); tone(1175, 0.5, 0.4, 0.05, 'sine')
        break
      case 'CONGRATS': // fanfare triplet → cannon pops
        tone(523.3, 0, 0.12, 0.06, 'square'); tone(659.3, 0.11, 0.12, 0.06, 'square')
        tone(784, 0.22, 0.3, 0.08, 'square')
        noise(0.32, 0.08, 0.09, 1400); noise(0.4, 0.08, 0.08, 1100)
        tone(1046.5, 0.5, 0.4, 0.12, 'triangle')
        break
      case 'ANNIVERSARY': // two notes rising separately, then a shared chord
        tone(440, 0, 0.5, 0.09, 'sine')
        tone(554.4, 0.2, 0.5, 0.09, 'sine')
        tone(440, 0.62, 0.55, 0.08, 'sine'); tone(554.4, 0.62, 0.55, 0.08, 'sine'); tone(659.3, 0.62, 0.6, 0.07, 'triangle')
        break
      case 'KNOCK':     // two soft raps on a door of light
        noise(0.22, 0.07, 0.20, 320); noise(0.32, 0.07, 0.18, 300)
        noise(0.60, 0.07, 0.20, 320); noise(0.70, 0.07, 0.18, 300)
        tone(523, 0.85, 0.4, 0.05, 'triangle')
        break
      default:
        tone(523, 0.05, 0.5, 0.1)
    }
    setTimeout(() => ctx.close(), 2500)
  } catch { /* audio blocked — visual + haptic still carry it */ }
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Detonation({ data, onSendBack, onDone }: Props) {
  const [phase, setPhase] = useState(0)
  const fired = useRef(false)
  const vibe = resolveVibe(data.vibeKey)

  useEffect(() => {
    if (fired.current) return
    fired.current = true
    const amp = data.intensity || 1.0
    if ('vibrate' in navigator) navigator.vibrate(vibe.haptic.map((v) => Math.round(v * amp)))
    playSignatureSound(vibe.key, amp)
    const t1 = setTimeout(() => setPhase(1), 550)
    const t2 = setTimeout(() => setPhase(2), 1050)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [vibe])

  const c = data.vibeColor || vibe.color

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000, background: '#000',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
    }}>
      {/* ambient flood */}
      <div style={{
        position: 'absolute', inset: 0,
        background: `radial-gradient(ellipse at center, ${c}26 0%, rgba(0,0,0,0.97) 74%)`,
        opacity: phase === 2 ? 0.5 : 1,
        transition: 'opacity 0.7s ease',
      }} />

      {/* THE SIGNATURE — a living organism of light */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1,
      }}>
        <Signature vibeKey={vibe.key} color={c} duration={1400} intensity={data.intensity || 1.0} />
      </div>

      {/* the word arrives after the feeling */}
      <div style={{
        position: 'relative', zIndex: 2, textAlign: 'center', padding: 24,
        pointerEvents: 'none',
      }}>
        <div className="syne" style={{
          fontWeight: 800,
          fontSize: phase >= 1 ? 'clamp(30px, 9vw, 44px)' : 0,
          letterSpacing: '-0.04em', textTransform: 'uppercase',
          color: '#ffffff', lineHeight: 1.05,
          transition: 'font-size 0.18s cubic-bezier(0.34,1.4,0.64,1)',
          textShadow: `0 2px 4px rgba(0,0,0,0.95), 0 3px 16px rgba(0,0,0,0.85), 0 0 34px ${c}, 0 0 60px ${c}bb`,
          WebkitTextStroke: `0.5px ${c}`,
        }}>
          {vibe.word}
        </div>
        {data.phrase && (
          <div style={{
            display: phase >= 2 ? 'inline-block' : 'none',
            fontSize: 16, fontStyle: 'italic',
            color: '#ffffff', marginTop: 16,
            fontWeight: 500,
            padding: '7px 16px',
            borderRadius: 100,
            background: 'rgba(0,0,0,0.42)',
            backdropFilter: 'blur(6px)',
            textShadow: '0 1px 8px rgba(0,0,0,0.95)',
          }}>
            {data.phrase}
          </div>
        )}
        {data.fromName && (
          <div className="eyebrow" style={{
            fontSize: phase >= 2 ? 10 : 0, marginTop: 12,
            transition: 'font-size 0.2s ease 0.1s',
          }}>
            {data.direction === 'sent' ? `sent to ${data.fromName}` : `from ${data.fromName}`}
          </div>
        )}
      </div>

      {/* reply bar */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '32px 24px calc(36px + env(safe-area-inset-bottom))',
        background: 'linear-gradient(to top, rgba(0,0,0,0.95) 55%, transparent)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 11,
        transform: phase >= 2 ? 'translateY(0)' : 'translateY(120px)',
        opacity: phase >= 2 ? 1 : 0,
        transition: 'transform 0.4s ease, opacity 0.4s ease',
        zIndex: 3,
      }}>
        {data.direction === 'received' && onSendBack && (
          <button className="pill grad" style={{ maxWidth: 300 }} onClick={onSendBack}>
            Send one back ↩
          </button>
        )}
        <button className="pill ghost" style={{ maxWidth: 300 }} onClick={onDone}>
          {data.direction === 'sent' ? 'Done' : 'Close'}
        </button>
      </div>
    </div>
  )
}
